import { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import { IWordsPhrasesModel } from "components/Conversations/Views/Drawer/components/DeepFilter/WordsPhrasesStore";
import { SlimLLMClassifierResponse } from "models/LLMs";
import {
    ChatSearchResult,
    TranscriptionSearchResult,
} from "components/Evaluation/Stores/EvalStore";
import { SpeakerStore } from "components/UI/AcxTranscription/Stores/SpeakerStore/SpeakerStore";
import _ from "lodash";
import moment from "moment";
import { isOpenEndedCallDuration } from "components/ApplicationFilters/FiltersAndChips/CallDuration/callDurationSliderState";
import { isOpenEndedMessageCount } from "components/ApplicationFilters/FiltersAndChips/MessageCount/messageCountSliderState";
import { Operator } from "models/Rule";
import {
    formatModuleScoreCondition,
    groupModuleScores,
} from "components/ApplicationFilters/FiltersAndChips/ModuleScores/moduleScoreLabels";
import {
    ApplicationFilters,
    ModuleScore,
    PrimaryDriver,
    PrimaryTopic,
    SimpleTheme,
    SimpleTopicCluster,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import {
    SafetyEvent,
    SafetyEventAsString,
    SafetyEventId,
    SEIdToString,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import { SmartBannerContext } from "stores/ContextStore";
import { IRootStore } from "stores/RootStore";
import { parseFromISO } from "utils/DateTimeUtils";
import { AcxTranscriptionStore } from "components/UI/AcxTranscription/Stores/AcxTranscriptionStore";
import { allOutlineSegmentOptions } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";

/* ------------ Facilitator Utils ------------ */

const hasOwnProperty = Object.hasOwn;

function createMessageSuffix(phrases: string[]): string {
    if (phrases.length === 0) return "";
    if (phrases.length === 1) return phrases[0];
    if (phrases.length === 2) return phrases.join(" and ");
    return `${phrases.slice(0, -1).join(", ")}, and ${
        phrases[phrases.length - 1]
    }`;
}

function isSingleDayRange(startDate?: string, endDate?: string): boolean {
    return (
        !!startDate &&
        !!endDate &&
        parseFromISO(startDate).isSame(parseFromISO(endDate), "day")
    );
}

function isSingleUtcDayRange(startDate?: string, endDate?: string): boolean {
    return (
        !!startDate &&
        !!endDate &&
        moment.utc(startDate).isSame(moment.utc(endDate), "day")
    );
}

function formatLongUtcDate(isoString: string): string {
    return moment.utc(isoString).format("MMMM D, YYYY");
}

function formatLongDate(isoString: string): string {
    return parseFromISO(isoString).format("MMMM D, YYYY");
}

function formatPatientJourneyStage(stage: PatientJourneyStage): string {
    switch (stage) {
        case PatientJourneyStage.BeforeTreatment:
            return "Before Treatment";
        case PatientJourneyStage.OnTreatment:
            return "On Treatment";
        case PatientJourneyStage.AfterTreatment:
            return "After Treatment";
        case PatientJourneyStage.RestartingTreatment:
            return "Restarting Treatment";
        case PatientJourneyStage.TreatmentDisruption:
            return "Treatment Disruption";
        case PatientJourneyStage.NoClearJourneyStage:
            return "No Clear Journey Stage";
        default:
            return "";
    }
}

function getPrimaryTopicLabel(
    filters: Partial<ApplicationFilters>,
): string | undefined {
    return filters.primaryTopics?.[0]?.topicLabel;
}

function getPrimaryDriverLabel(
    filters: Partial<ApplicationFilters>,
): string | undefined {
    return filters.drivers?.[0]?.driverName;
}

function getJourneyStageLabel(
    filters: Partial<ApplicationFilters>,
): string | undefined {
    const stage = filters.patientJourneyStages?.[0];
    return stage !== undefined ? formatPatientJourneyStage(stage) : undefined;
}

/**
 * How a module's score conditions read in a banner.
 *
 * A module's conditions are ANDed, so they read as one restriction on that module rather than as
 * separate filters naming it twice, the same way the chips phrase them. A lower bound with an upper
 * one is a range and reads as "80–85%", matching the bar a drilldown came from and the way every
 * other range in the product spells itself. Anything else keeps the comparisons in symbols, which
 * is what a chip shows and what reads shortest in a sentence already carrying a module name and a
 * percentage.
 *
 * A module whose name has not loaded is phrased without one. The list arrives with the filter value
 * store and is normally present, and a sentence reading "the module score" is worth more than one
 * quoting an id at the reader.
 */
function getModuleTitleById(rootStoreRef: IRootStore): Map<string, string> {
    return new Map(
        (
            rootStoreRef.getStore(ApplicationFilterValuesStore)
                .hierarchyLicensedModules ?? []
        ).map((module) => [module.id, module.title]),
    );
}

export function phraseForModuleScores(
    moduleScores: ModuleScore[],
    titleById: Map<string, string>,
): string {
    return groupModuleScores(moduleScores)
        .map((group) => {
            const title = titleById.get(group.licensedModuleId);
            const subject = title ? `the ${title} score` : "the module score";
            const conditions = group.conditions.map((row) => row.entry);

            return `${subject} is ${orderModuleScoreRange(conditions)
                .map((entry) =>
                    formatModuleScoreCondition(entry, true).toLowerCase(),
                )
                .join(" and ")}`;
        })
        .join(", and ");
}

/**
 * A module's conditions, lower bound first when they are a range.
 *
 * Each condition is written out in words, so a range says exactly which ends it includes:
 * "greater than or equal to 80% and less than 90%" rather than "80–90%", which reads as though 90
 * is in it (AUT-10832). A lower bound ANDed with an upper one reads in that order whichever way
 * the filter stores them. Anything else keeps the order it was given in.
 */
function orderModuleScoreRange(conditions: ModuleScore[]): ModuleScore[] {
    if (conditions.length !== 2) return conditions;

    const lower = conditions.find(
        (condition) =>
            condition.operator === Operator.GtEq ||
            condition.operator === Operator.Gt,
    );
    const upper = conditions.find(
        (condition) =>
            condition.operator === Operator.Lt ||
            condition.operator === Operator.LtEq,
    );

    return lower && upper ? [lower, upper] : conditions;
}

/**
 * The conditions of the first `moduleCount` modules, as a new array.
 *
 * The banner's value budget counts a module once however many conditions build it, so it has to
 * trim by module too: trimming rows cut a range down to its first bound and named a wider set of
 * conversations than the ones shown (AUT-10917).
 */
function keepWholeModules(
    moduleScores: ModuleScore[],
    moduleCount: number,
): ModuleScore[] {
    return groupModuleScores(moduleScores)
        .slice(0, Math.max(moduleCount, 0))
        .flatMap((group) => group.conditions)
        .sort((a, b) => a.index - b.index)
        .map((row) => row.entry);
}

function getModelResultValue(
    filters: Partial<ApplicationFilters>,
    classifierName: string,
): string | undefined {
    const classifier = filters.modelResults?.find((result) =>
        result.name.toLowerCase().includes(classifierName.toLowerCase()),
    );

    return classifier?.results?.[0];
}

function buildProactiveInsightMessage(
    context: Extract<
        SmartBannerContext,
        { actionTaken: "ViewInConversations" }
    >,
    filters: Partial<ApplicationFilters>,
): string | null {
    if (
        context.source !== "proactiveInsights" ||
        !context.hierarchyName ||
        typeof filters.startDate !== "string" ||
        typeof filters.endDate !== "string" ||
        !context.proactiveInsightType
    ) {
        return null;
    }

    const prefix = `You are viewing these conversations from ${formatLongUtcDate(
        filters.startDate,
    )} to ${formatLongUtcDate(filters.endDate)} in ${
        context.hierarchyName
    } because `;

    const driver = getPrimaryDriverLabel(filters);
    const topic = getPrimaryTopicLabel(filters);
    const modelSubject = driver ?? topic;
    const modelSubjectLabel = driver ? "primary driver" : "primary topic";
    const journeyStage = getJourneyStageLabel(filters);
    const repeatCallerResult = getModelResultValue(filters, "Repeat Caller");
    const callResolutionResult = getModelResultValue(
        filters,
        "Call Resolution",
    );

    let reason: string | null = null;

    switch (context.proactiveInsightType) {
        case "EddyRateByTopic":
            reason =
                modelSubject !== undefined
                    ? `Eddy Effect is present, and the ${modelSubjectLabel} is ${modelSubject}`
                    : null;
            break;
        case "EddyRateByJourneyStage":
            reason =
                journeyStage !== undefined
                    ? `Eddy Effect is present, and the journey stage is ${journeyStage}`
                    : null;
            break;
        case "RepeatCallsByTopic":
            reason =
                modelSubject !== undefined && repeatCallerResult !== undefined
                    ? `the ${modelSubjectLabel} is ${modelSubject} and the result for 'Repeat Caller' is ${repeatCallerResult}`
                    : null;
            break;
        case "DriversOfTreatmentDisruption":
            reason =
                modelSubject !== undefined
                    ? `the ${modelSubjectLabel} is ${modelSubject} and the journey stage is Treatment Disruption`
                    : null;
            break;
        case "DriversOfUnresolvedCalls":
            reason =
                modelSubject !== undefined && callResolutionResult !== undefined
                    ? `the ${modelSubjectLabel} is ${modelSubject} and the result for 'Call Resolution' is ${callResolutionResult}`
                    : null;
            break;
        case "UnresolvedRepeatCalls":
            reason =
                callResolutionResult !== undefined &&
                repeatCallerResult !== undefined
                    ? `the result for 'Call Resolution' is ${callResolutionResult} and the result for 'Repeat Caller' is ${repeatCallerResult}`
                    : null;
            break;
        case "BeforeTreatmentEddiesByTopic":
            reason =
                modelSubject !== undefined
                    ? `Eddy Effect is present, the journey stage is Before Treatment, and the ${modelSubjectLabel} is ${modelSubject}`
                    : null;
            break;
        case "TreatmentDisruptionRate":
            reason = "the journey stage is Treatment Disruption";
            break;
        case "RepeatCallRate":
            reason =
                repeatCallerResult !== undefined
                    ? `the result for 'Repeat Caller' is ${repeatCallerResult}`
                    : null;
            break;
        case "CallResolutionRate":
            reason =
                callResolutionResult !== undefined
                    ? `the result for 'Call Resolution' is ${callResolutionResult}`
                    : null;
            break;
    }

    return reason ? `${prefix}${reason}.` : null;
}

function getCoreAiFilterTitle(
    filterKey: keyof ApplicationFilters,
    rootStoreRef: IRootStore,
): string | undefined {
    const filterGroup = rootStoreRef
        .getStore(ApplicationFilterItemsStore)
        .filterGroups.find((group) => group.title === "Core AI Models");
    const filterItem = filterGroup?.items.find(
        (item) => item.filterKey === filterKey,
    );
    return filterItem?.title;
}

function formatGenericBannerValue(filterValue: unknown): string {
    if (typeof filterValue === "string") {
        return filterValue;
    }

    if (typeof filterValue === "boolean") {
        return filterValue ? "true" : "false";
    }

    if (Array.isArray(filterValue)) {
        const values = filterValue
            .flatMap((value) => {
                if (typeof value === "string") {
                    return value;
                }

                if (
                    value &&
                    typeof value === "object" &&
                    "name" in value &&
                    typeof value.name === "string"
                ) {
                    return value.name;
                }

                return [];
            })
            .filter((value) => value.length > 0);

        return createMessageSuffix(values);
    }

    return "";
}

/* ------------ Value Extracting ------------ */

// Different filters have different value shapes, so we need to extract the relevant value for phrasing
// Right now, we only grab the first value for multi-value filters
function extractFilterValue(
    filterKey: keyof ApplicationFilters,
    rawValue: unknown,
    rootStoreRef: IRootStore,
): { extractedValue: unknown; valuesCount: number } {
    switch (filterKey) {
        // Actually Themes, via the legacy Fast Reporting Topic path
        case "topics": {
            const firstTheme = (
                rawValue as SimpleTopicCluster[] | undefined
            )?.[0];
            return {
                extractedValue: firstTheme?.topicLabel,
                valuesCount: firstTheme?.topicLabel ? 1 : 0,
            };
        }
        // Themes via the Primary Driver
        case "themes": {
            const firstTheme = (rawValue as SimpleTheme[] | undefined)?.[0];
            return {
                extractedValue: firstTheme?.themeLabel,
                valuesCount: firstTheme?.themeLabel ? 1 : 0,
            };
        }
        case "primaryTopics": {
            const topics = rawValue as PrimaryTopic[] | undefined;
            if (!topics || topics.length === 0) {
                return { extractedValue: [], valuesCount: 0 };
            }
            return {
                extractedValue: topics.map((topic) => topic.topicLabel),
                valuesCount: 1,
            };
        }
        case "drivers": {
            const firstDriver = (rawValue as PrimaryDriver[] | undefined)?.[0];
            return {
                extractedValue: firstDriver?.driverName,
                valuesCount: firstDriver?.driverName ? 1 : 0,
            };
        }
        case "rbcFilterItems": {
            for (const rbc of rawValue as IRBCFilter[]) {
                // OR detected in Classifier Filter Object, so we can just say "includes one or more classifiers"
                // We can only do this if the value is true (i.e. "Includes") and not false (i.e. "Does Not Include")
                if (rbc.combinator === 1 && rbc.value) {
                    return {
                        extractedValue: true,
                        valuesCount: 1,
                    };
                }
            }

            const classifierIds = (
                rawValue as IRBCFilter[] | undefined
            )?.flatMap((classifier) => {
                // Return empty array if the Classifier Filter Object is a "Does Not Include" filter (i.e. value is false)
                if (!classifier.value) return [];
                return classifier.classifierIds;
            });

            const foundClassifiers = classifierIds?.map((classifierId) => {
                return {
                    name: rootStoreRef
                        .getStore(ApplicationFilterValuesStore)
                        .userClassifiers?.find(
                            (classifier) => classifier.id === classifierId,
                        )?.name,
                    id: classifierId,
                };
            });

            if (!foundClassifiers || foundClassifiers.length === 0)
                return { extractedValue: "", valuesCount: 0 };
            if (foundClassifiers.length === 1)
                return {
                    extractedValue: [
                        {
                            name: foundClassifiers[0].name,
                            id: foundClassifiers[0].id,
                        },
                    ],
                    valuesCount: 1,
                };

            return {
                extractedValue: foundClassifiers,
                valuesCount: foundClassifiers.length,
            };
        }
        case "safetyEvent": {
            const safetyEvent = rawValue as SafetyEvent | undefined;
            return safetyEvent &&
                (safetyEvent.identification !== null ||
                    safetyEvent.types.length > 0 ||
                    safetyEvent.acknowledgments.length > 0)
                ? {
                      extractedValue: SafetyEventAsString(safetyEvent),
                      valuesCount: 1,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        // Words and Phrases and the below Words and Phrases Search String are the Standard and Advanced filter options
        // Only one will be present at a time
        case "wordsAndPhrasesItems": {
            const firstPhrase = (
                rawValue as IWordsPhrasesModel[] | undefined
            )?.[0];
            return firstPhrase?.searchPhrase
                ? {
                      extractedValue: true,
                      valuesCount: 1,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "wordsAndPhrasesSearchString": {
            const searchString = rawValue as string | undefined;
            return searchString
                ? { extractedValue: true, valuesCount: 1 }
                : { extractedValue: "", valuesCount: 0 };
        }

        case "eddyEffectStatus": {
            const status = rawValue as boolean | undefined;
            return status !== undefined
                ? { extractedValue: status, valuesCount: 1 }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "moduleScores": {
            const moduleScores = rawValue as ModuleScore[] | undefined;
            if (!moduleScores || moduleScores.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }

            // Counted by module rather than by condition: a module restricted to a range is one
            // restriction to a reader, and one chip, however many rows built it.
            return {
                extractedValue: moduleScores,
                valuesCount: groupModuleScores(moduleScores).length,
            };
        }
        case "modelResults": {
            const modelResults = rawValue as
                | SlimLLMClassifierResponse[]
                | undefined;
            return modelResults && modelResults.length > 0
                ? {
                      extractedValue: modelResults.map((mr) => ({
                          name: mr.name,
                          results: mr.results,
                      })),
                      valuesCount: modelResults.length,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "agentIds": {
            const agentIds = rawValue as string[] | undefined;
            if (!agentIds || agentIds.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }
            const agentNames = rootStoreRef
                .getStore(ApplicationFilterValuesStore)
                .agents.filter((agent) => agentIds.includes(agent.id))
                .map((agent) => agent.fullName);
            return agentNames && agentNames.length > 0
                ? {
                      extractedValue: agentNames,
                      valuesCount: agentNames.length,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "agentManagerIds": {
            const agentManagerIds = rawValue as string[] | undefined;
            if (!agentManagerIds || agentManagerIds.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }
            const managerNames = rootStoreRef
                .getStore(ApplicationFilterValuesStore)
                .agentManagers.filter((manager) =>
                    agentManagerIds.includes(manager.id),
                )
                .map((manager) => manager.fullName?.trim())
                .filter((name): name is string => !!name);
            return managerNames.length > 0
                ? {
                      extractedValue: managerNames,
                      valuesCount: managerNames.length,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "contactTypes": {
            const contactTypes = rawValue as string[] | undefined;
            if (!contactTypes || contactTypes.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }

            const applicationFilterValuesStore = rootStoreRef.getStore(
                ApplicationFilterValuesStore,
            );
            const contactTypeLabels = contactTypes.map((contactType) =>
                applicationFilterValuesStore.getContactTypeLabel(contactType),
            );
            return {
                extractedValue: contactTypeLabels,
                valuesCount: contactTypeLabels.length,
            };
        }
        case "beginningSentiment":
        case "endingSentiment": {
            const sentiment = rawValue as Sentiment | undefined;
            return sentiment !== undefined
                ? {
                      extractedValue: Sentiment.toString(sentiment),
                      valuesCount: 1,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "hipaaCompliance": {
            const hipaaCompliance = rawValue as HipaaCompliance | undefined;
            return hipaaCompliance !== undefined
                ? {
                      extractedValue: HipaaCompliance.toString(hipaaCompliance),
                      valuesCount: 1,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "hierarchyIds": {
            const hierarchyIds = rawValue as string[] | undefined;
            return hierarchyIds && hierarchyIds.length > 0
                ? {
                      extractedValue: hierarchyIds.length,
                      valuesCount: hierarchyIds.length,
                  }
                : { extractedValue: "", valuesCount: 0 };
        }
        case "patientJourneyStages": {
            const patientJourneyStages = rawValue as
                | PatientJourneyStage[]
                | undefined;
            if (!patientJourneyStages || patientJourneyStages.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }

            return {
                extractedValue: patientJourneyStages.map((stage) =>
                    formatPatientJourneyStage(stage),
                ),
                valuesCount: patientJourneyStages.length,
            };
        }
        case "outlineSegmentFilterItems": {
            const outlineSegmentFilterItems = (
                rawValue as { value: boolean; segments: string[] }[] | undefined
            )?.filter((item) => item.value);
            if (
                !outlineSegmentFilterItems ||
                outlineSegmentFilterItems.length === 0
            ) {
                return { extractedValue: "", valuesCount: 0 };
            }

            const outlineSegments = outlineSegmentFilterItems
                .flatMap((item) => item.segments)
                .map(
                    (segmentId) =>
                        allOutlineSegmentOptions.find(
                            (option) => option.id === segmentId,
                        )?.name ?? segmentId,
                );

            return {
                extractedValue: outlineSegments,
                valuesCount: outlineSegments.length,
            };
        }
        case "callDirections": {
            const callDirections = rawValue as CallDirection[] | undefined;
            if (!callDirections || callDirections.length === 0) {
                return { extractedValue: "", valuesCount: 0 };
            }

            return {
                extractedValue: callDirections.map((callDirection) =>
                    CallDirection.toString(callDirection),
                ),
                valuesCount: callDirections.length,
            };
        }
        default:
            return { extractedValue: "", valuesCount: 0 };
    }
}

/* ------------ Phrasing Utils ------------ */

/**
 * How a bounded range reads in a banner.
 *
 * Built from the filter pair rather than from one key's value, because neither bound describes the
 * range alone. Both keys phrase to this same sentence and the caller drops a phrase it already has,
 * so the range is stated once however many of the two are present.
 *
 * A range running to the top of its filter's own scale is the open-ended one a drilldown into the
 * last bucket produces, and reads as "20+ minutes" or "31+ messages". Both bounds absent is the
 * filter's "any" state, which is not worth saying at all.
 *
 * An unset lower bound reads as zero, the same way each filter's own slider shows it, and the
 * separator is an en dash to match the bucket labels the chart puts on its bars.
 */
function phraseForRange(options: {
    min: number | undefined;
    max: number | undefined;
    subject: string;
    unit: string;
    isOpenEnded: (min?: number, max?: number) => boolean;
}): string {
    const { min, max, subject, unit, isOpenEnded } = options;

    if (!max && !min) return "";
    if (isOpenEnded(min, max)) return `${subject} is ${min}+ ${unit}`;

    return `${subject} is ${min ?? 0}–${max} ${unit}`;
}

/**
 * How a call-duration range reads in a banner. The maximum is exclusive, but it reads as the number
 * the range stops at either way, the difference being one millisecond value.
 */
export function phraseForCallDurationRange(
    filters: Partial<ApplicationFilters>,
): string {
    return phraseForRange({
        // Zero is how both bounds of this filter spell "no bound", so it is read as absent rather
        // than as a real zero, matching every other place that applies the pair.
        min: filters.minCallDuration || undefined,
        max: filters.maxCallDuration || undefined,
        subject: "the call duration",
        unit: "minutes",
        isOpenEnded: isOpenEndedCallDuration,
    });
}

/**
 * How a message-count range reads in a banner.
 *
 * Both bounds are inclusive here, which is what a message-count bucket's own edges are, so "6–10
 * messages" is 6 through 10 and the sentence names exactly the conversations the bar counted.
 */
export function phraseForMessageCountRange(
    filters: Partial<ApplicationFilters>,
): string {
    return phraseForRange({
        min: filters.minMessageCount || undefined,
        max: filters.maxMessageCount || undefined,
        subject: "the message count",
        unit: "messages",
        isOpenEnded: isOpenEndedMessageCount,
    });
}

// Different filters have different phrasings defined by product
// If a filter key does not have a defined phrasing, the filter is considered not supported for the smart banner
function phraseForFilter(
    filterKey: keyof ApplicationFilters,
    filterValue: unknown,
    rootStoreRef: IRootStore,
    filters: Partial<ApplicationFilters>,
): string {
    switch (filterKey) {
        // The two bounds are one filter to a reader, so both keys produce the whole range. They
        // deliberately contribute no value count: the range is always shown rather than competing
        // for the banner's value budget.
        case "minCallDuration":
        case "maxCallDuration":
            return phraseForCallDurationRange(filters);
        case "minMessageCount":
        case "maxMessageCount":
            return phraseForMessageCountRange(filters);
        case "moduleScores": {
            const moduleScores = filterValue as ModuleScore[] | undefined;
            return moduleScores && moduleScores.length > 0
                ? phraseForModuleScores(
                      moduleScores,
                      getModuleTitleById(rootStoreRef),
                  )
                : "";
        }
        // Both Theme filters share this phrasing verbatim; only the source filter differs.
        case "topics":
        case "themes":
            return filterValue ? `the primary theme is ${filterValue}` : "";
        case "primaryTopics":
            // In the case of phraseForFilter, there should only ever be one primary topic
            const topics = filterValue as string[];
            return topics.length > 0 ? `the primary topic is ${topics[0]}` : "";
        case "drivers":
            return filterValue ? `the primary driver is ${filterValue}` : "";
        case "eddyEffectStatus":
            return filterValue
                ? "Eddy Effect is present"
                : "Eddy Effect is not present";
        case "rbcFilterItems":
            return Array.isArray(filterValue)
                ? `the classifier is ${filterValue[filterValue.length - 1]}`
                : "";
        case "safetyEvent":
            return filterValue ? `the safety event was ${filterValue}` : "";
        case "modelResults":
            const modelResults = filterValue as {
                name: string;
                results: string[];
            }[];
            let returnString = "";
            if (modelResults && modelResults.length > 0) {
                modelResults.forEach((modelResult, index) => {
                    returnString += `the result for '${
                        modelResult.name
                    }' is ${modelResult.results.join(", ")}`;
                    if (index < modelResults.length - 1) {
                        returnString += ", and ";
                    }
                });
            }
            return returnString;
        case "agentIds":
            if (
                filterValue &&
                Array.isArray(filterValue) &&
                filterValue.length > 1
            ) {
                return `the agents are ${filterValue.join(", ")}`;
            }
            return filterValue ? `the agent is ${filterValue}` : "";
        case "agentManagerIds":
            if (
                filterValue &&
                Array.isArray(filterValue) &&
                filterValue.length > 1
            ) {
                return `the Agent Managers are ${filterValue.join(", ")}`;
            }
            return filterValue ? `the Agent Manager is ${filterValue}` : "";
        case "hierarchyIds":
            if (typeof filterValue !== "number" || filterValue <= 0) {
                return "";
            }
            return filterValue === 1
                ? "the selected hierarchy is applied"
                : "the selected hierarchies are applied";
        case "patientJourneyStages":
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return "";
            }
            return `the journey stage is ${filterValue[0]}`;
        default:
            const coreAiFilterTitle = getCoreAiFilterTitle(
                filterKey,
                rootStoreRef,
            );
            if (!coreAiFilterTitle) {
                return "";
            }

            const formattedValue = formatGenericBannerValue(filterValue);
            return formattedValue
                ? `${coreAiFilterTitle} is ${formattedValue}`
                : "";
    }
}

function phrasingForFocusConversation(
    filterKey: keyof ApplicationFilters,
    filterValue: unknown,
    context: SmartBannerContext,
): string {
    // We should never be getting phrasing for focus conversation if the action taken is not FocusConversation
    if (context.actionTaken !== "FocusConversation") return "";
    switch (filterKey) {
        case "primaryTopics":
            // For focus conversation, our filters can have multiple primary topics
            // We want to see if any of the top 3 topics from the conversation are in the applied primary topics filter
            // If so, we want to display that topic in the message
            const topics = filterValue as string[];
            // Pull the top 3 topics from the conversation store that are also in the applied primary topics filter
            const conversationTopTopics =
                context.conversationStore.legacyTopicInsights
                    .map((topic) => topic.name)
                    .filter((topic) => topics.includes(topic));

            // We only want to display the first one, even if there are multiple matches
            return topics.length > 0
                ? `the primary topic <span style="color:#3664D6; font-weight:bold;">${conversationTopTopics[0]}</span>`
                : "";
        case "eddyEffectStatus":
            return filterValue
                ? `the <span style="color:#3664D6; font-weight:bold;">Eddy Effect</span>`
                : "";
        case "rbcFilterItems":
            // TODO: May need to receive count of used parts, and then grab the values with the available count left
            // e.g. 2 parts used. 2 parts left. Grab 2 values from the filterValue array, and create the message suffix with those 2 values
            if (Array.isArray(filterValue)) {
                const formattedValues = filterValue.map(
                    (value) =>
                        `<span style="color:#3664D6; font-weight:bold;">${value}</span>`,
                );
                return `the classifier${
                    formattedValues.length > 1 ? "s" : ""
                } ${createMessageSuffix(formattedValues)}`;
            }

            if (typeof filterValue === "boolean" && filterValue === true) {
                return `<span style="color:#3664D6; font-weight:bold;">one or more classifiers</span>`;
            }
            return filterValue
                ? `the classifier <span style="color:#3664D6; font-weight:bold;">${filterValue}</span>`
                : "";
        case "safetyEvent":
            return filterValue
                ? `a <span style="color:#3664D6; font-weight:bold;">safety event</span>`
                : "";
        case "wordsAndPhrasesItems":
        case "wordsAndPhrasesSearchString":
            return filterValue
                ? `your defined <span style="color:#3664D6; font-weight:bold;">words and phrases</span>`
                : "";
        default:
            return "";
    }
}

function pruneEmptyFilters(
    filters: Partial<ApplicationFilters>,
): Partial<ApplicationFilters> {
    const cleaned: Partial<ApplicationFilters> = {};
    for (const [filterKey, rawValue] of Object.entries(filters) as [
        keyof ApplicationFilters,
        any,
    ][]) {
        if (rawValue == null) continue;
        if (Array.isArray(rawValue) && rawValue.length === 0) continue;
        cleaned[filterKey] = rawValue;
    }
    return cleaned;
}

/* ------------ Smart Banner Message Builder ------------ */

const WORDS_AND_PHRASES_IS_INPUT_STANDARD_KEY =
    "wordsAndPhrasesIsInputStandard";
const WORDS_AND_PHRASES_SEARCH_STRING_KEY = "wordsAndPhrasesSearchString";
const WORDS_AND_PHRASES_ITEMS_KEY = "wordsAndPhrasesItems";

const PRIORITY_ORDERED_FILTER_KEYS: (keyof ApplicationFilters)[] = [
    "eddyEffectStatus",
    "patientJourneyStages",
    "safetyEvent",
    "wordsAndPhrasesItems",
    "wordsAndPhrasesSearchString",
    "primaryTopics",
    "modelResults",
    "rbcFilterItems",
];

const MAX_VALUES_IN_BANNER = 4;

// Build the parts of the message based on the filters applied
function buildMessageParts(
    rootStoreRef: IRootStore,
    context: SmartBannerContext,
): { messageParts: string[]; filterCountNotShown: number } {
    const messageParts: string[] = [];

    if (
        context.actionTaken === "ViewInConversations" &&
        context.selectedSavedFilter
    ) {
        messageParts.push(
            `your saved filter, '${context.selectedSavedFilter.name}' was applied`,
        );
    }

    let valuesUsed = 0;

    // ONE OFF Logic to Support Words and Phrases Filter (Standard and Advanced)
    // We have to use one or the other, never both
    // We use the presence of the "wordsAndPhrasesIsInputStandard" boolean to determine which is being used
    if (
        hasOwnProperty(
            context.filters,
            WORDS_AND_PHRASES_IS_INPUT_STANDARD_KEY,
        ) &&
        context.filters[WORDS_AND_PHRASES_IS_INPUT_STANDARD_KEY]
    ) {
        delete context.filters[WORDS_AND_PHRASES_SEARCH_STRING_KEY];
    } else {
        delete context.filters[WORDS_AND_PHRASES_ITEMS_KEY];
    }

    // We have a priority order of filter keys to include in the message
    // First add the priority keys that are ONLY present in the filters provided
    // Then add any other keys that are present in the filters provided
    const orderedKeys: (keyof ApplicationFilters)[] = [
        ...(PRIORITY_ORDERED_FILTER_KEYS.filter((key) =>
            hasOwnProperty(context.filters, key),
        ) as (keyof ApplicationFilters)[]),
        ...(
            Object.keys(
                context.filters as ApplicationFilters,
            ) as (keyof ApplicationFilters)[]
        ).filter(
            (key) =>
                !PRIORITY_ORDERED_FILTER_KEYS.includes(key) &&
                key !== "startDate" &&
                key !== "endDate",
        ),
    ];
    for (const filterKey of orderedKeys) {
        const rawValue = (context.filters as Partial<ApplicationFilters>)[
            filterKey
        ];

        let { extractedValue, valuesCount } = extractFilterValue(
            filterKey,
            rawValue,
            rootStoreRef,
        );

        /* ------------ ONE OFF Business Logic; Tickets have been added to remove ------------ */
        // ONE OFF Business Logic for Focused Conversation and Safety Event
        // Don't include "Not Identified" safety event in the message or count it as a value used
        if (
            context.actionTaken === "FocusConversation" &&
            filterKey === "safetyEvent" &&
            typeof extractedValue === "string" &&
            extractedValue.includes(SEIdToString(SafetyEventId.NotIdentified))
        ) {
            continue;
        }

        // ONE OFF Business Logic for Focused Conversation and Primary Topic
        // Don't include "Is Not One Of" primary topic in the message or count it as a value used
        if (
            context.actionTaken === "FocusConversation" &&
            filterKey === "primaryTopics" &&
            !context.filters.containsPrimaryTopics
        ) {
            continue;
        }

        // If we are showing rbc filter items, we need to sort them by score within the conversation
        // Also only want to show classifiers that are actually present in the conversation
        if (
            context.actionTaken === "FocusConversation" &&
            filterKey === "rbcFilterItems" &&
            Array.isArray(extractedValue) &&
            extractedValue.length > 0 &&
            context.conversationStore.selectedConversation &&
            context.conversationStore.selectedConversation
                .conversationClassifiers.length > 0
        ) {
            // The extracted value is an array of classifier names and ids for rbcFilterItems
            const extractedClassifiers = extractedValue as {
                name: string;
                id: string;
            }[];

            // Grab the conversationClassifiers and filter to only ones in our extractedClassifiers
            const filteredClassifiers =
                context.conversationStore.selectedConversation.conversationClassifiers.filter(
                    (classifier) =>
                        extractedClassifiers
                            .map((item) => item.id)
                            .includes(classifier.classifierId),
                );

            // Update the extracted value to just be the names of the filtered classifiers, sorted by score
            if (filteredClassifiers.length > 0) {
                filteredClassifiers.sort((a, b) => b.score - a.score);
                extractedValue = filteredClassifiers.map(
                    (classifier) => classifier.name,
                );
            }
        }

        // We need to continue accumulating valuesUsed even if we don't use the value in the message
        // so we can display how many additional filters are applied but not shown
        if (valuesUsed > 4) {
            valuesUsed += valuesCount;
            continue;
        }

        const availableValues = MAX_VALUES_IN_BANNER - valuesUsed;
        if (filterKey === "moduleScores" && Array.isArray(extractedValue)) {
            extractedValue = keepWholeModules(
                extractedValue as ModuleScore[],
                availableValues,
            );
        } else if (
            Array.isArray(extractedValue) &&
            extractedValue.length > availableValues &&
            // ONE OFF logic - we need all the topics to determine if any match the conversation.
            // We will only display one anyway in the phrasing logic
            filterKey !== "primaryTopics"
        ) {
            extractedValue.length = availableValues;
        }

        // Different phrasing functions are used based on what action is causing the smart banner to render
        if (context.actionTaken === "FocusConversation") {
            const phrase = phrasingForFocusConversation(
                filterKey,
                extractedValue,
                context,
            );
            if (phrase && !messageParts.includes(phrase)) {
                messageParts.push(phrase);
            }
        } else if (context.actionTaken === "ViewInConversations") {
            if (
                Array.isArray(extractedValue) &&
                extractedValue.length > 0 &&
                filterKey === "rbcFilterItems"
            ) {
                // Grab the names from the objects
                extractedValue = extractedValue.map((val) => val.name);
            }
            const phrase = phraseForFilter(
                filterKey,
                extractedValue,
                rootStoreRef,
                context.filters as Partial<ApplicationFilters>,
            );
            if (phrase && !messageParts.includes(phrase)) {
                messageParts.push(phrase);
            }
        }

        // Accumulate count of values used
        valuesUsed += valuesCount;
    }

    return {
        messageParts,
        filterCountNotShown:
            valuesUsed > MAX_VALUES_IN_BANNER
                ? valuesUsed - MAX_VALUES_IN_BANNER
                : 0,
    };
}

/* ------------ Facilitator with Business Logic ------------ */

export type SmartBannerBlueprint =
    | { type: "mediaTypes"; props: { mediaTypes: any[] } }
    | {
          type: "conversationExplorerFilters";
          smartBannerMessage: string;
      }
    | {
          type: "focusConversationFilters";
          smartBannerMessage: string;
          searchResults: (TranscriptionSearchResult | ChatSearchResult)[];
          speakerStore: SpeakerStore;
          searchItems: IWordsPhrasesModel[];
          transcriptionStore: AcxTranscriptionStore;
          onClick: () => void;
      }
    | { type: "searchMatch"; props: { searchTerm: string } }
    | { type: "transcriptSnippet"; props: { snippet: string } };

export function buildSmartBannerBlueprint(
    context: SmartBannerContext,
    rootStoreRef: IRootStore,
): SmartBannerBlueprint | null {
    // This will have to change as more types of types of smart banners are added
    // Maybe we always include the possiblity of a message for some types
    // context.shouldIncludeMessage ? No... I think we should determine that here
    // I do think we should include in the blueprint... bluePrint.shouldIncludeMessage = true/false

    const additionalFilters = pruneEmptyFilters(context.filters ?? {});

    // Below is the business logic for creating the Smart Banner Blueprint
    switch (context.actionTaken) {
        case "ViewInConversations": {
            const proactiveInsightMessage = buildProactiveInsightMessage(
                context,
                additionalFilters,
            );

            if (proactiveInsightMessage) {
                return {
                    type: "conversationExplorerFilters",
                    smartBannerMessage: proactiveInsightMessage,
                };
            }

            const includesDates =
                hasOwnProperty(additionalFilters, "startDate") &&
                hasOwnProperty(additionalFilters, "endDate");

            const isSingleDay = isSingleDayRange(
                additionalFilters.startDate as string | undefined,
                additionalFilters.endDate as string | undefined,
            );
            const isSingleProactiveInsightUtcDay =
                context.source === "proactiveInsights"
                    ? isSingleUtcDayRange(
                          additionalFilters.startDate as string | undefined,
                          additionalFilters.endDate as string | undefined,
                      )
                    : false;

            // This builds the parts of the message based on the filters applied
            // e.g. eddyEffectStatus and topics are included in the additionalFilters -> "Eddy Effect is present", "the primary theme is X"
            const { messageParts, filterCountNotShown } = buildMessageParts(
                rootStoreRef,
                context,
            );

            const messagePrefix =
                context.source === "proactiveInsights" &&
                context.hierarchyName &&
                includesDates &&
                typeof additionalFilters.startDate === "string"
                    ? isSingleProactiveInsightUtcDay
                        ? `You are viewing these conversations from ${formatLongUtcDate(
                              additionalFilters.startDate,
                          )} in ${context.hierarchyName} because `
                        : `You are viewing these conversations from ${formatLongUtcDate(
                              additionalFilters.startDate,
                          )} to ${formatLongUtcDate(
                              additionalFilters.endDate as string,
                          )} in ${context.hierarchyName} because `
                    : includesDates &&
                      typeof additionalFilters.startDate === "string"
                    ? isSingleDay
                        ? `These conversations from ${formatLongDate(
                              additionalFilters.startDate,
                          )} were surfaced because `
                        : `These conversations from ${formatLongDate(
                              additionalFilters.startDate,
                          )} to ${formatLongDate(
                              additionalFilters.endDate as string,
                          )} were surfaced because `
                    : `These conversations were surfaced because `;

            // This CAN and SHOULD be updated as the messaging is updated by product
            // For now, we have a messagePrefix that contains a date or not
            // and a list of message parts joined together
            let smartBannerMessage =
                messageParts.length > 0
                    ? `${messagePrefix}${createMessageSuffix(messageParts)}`
                    : "";

            // If there is no message, no blueprint is returned
            if (!smartBannerMessage) return null;

            if (filterCountNotShown > 0) {
                smartBannerMessage += ` — <span style="font-style:italic;"> with ${filterCountNotShown} additional filter${
                    filterCountNotShown > 1 ? "s" : ""
                }.</span>`;
            } else {
                smartBannerMessage += ".";
            }

            return {
                type: "conversationExplorerFilters",
                smartBannerMessage,
            };
        }
        case "FocusConversation": {
            const { messageParts, filterCountNotShown } = buildMessageParts(
                rootStoreRef,
                context,
            );

            let smartBannerMessage =
                messageParts.length > 0
                    ? `This conversation includes ${createMessageSuffix(
                          messageParts,
                      )}`
                    : "";

            if (!smartBannerMessage) return null;

            if (filterCountNotShown > 0) {
                smartBannerMessage += ` — <span style="font-style:italic;"> with ${filterCountNotShown} additional filter${
                    filterCountNotShown > 1 ? "s" : ""
                }.</span>`;
            } else {
                smartBannerMessage += ".";
            }

            const searchItems = context.filters.wordsAndPhrasesItems
                ? (context.filters.wordsAndPhrasesItems as IWordsPhrasesModel[])
                : [];

            let searchResults: (
                | TranscriptionSearchResult
                | ChatSearchResult
            )[] = [];
            if (
                context.filters.wordsAndPhrasesItems &&
                smartBannerMessage.includes("words and phrases") &&
                context.transcriptionStore &&
                context.conversationStore.selectedConversation
            ) {
                searchItems.forEach((item) => {
                    if (item.value === "Does not contain") {
                        // Skip "Does not contain" searches, since we can't get results for those
                        return;
                    }

                    context.transcriptionStore.setSearchString(
                        item.searchPhrase,
                    );

                    let results:
                        | ChatSearchResult[]
                        | TranscriptionSearchResult[]
                        | undefined = undefined;
                    if (
                        context.conversationStore.selectedConversation!.isChat
                    ) {
                        // Chat, use getChatSearchResults
                        results =
                            context.transcriptionStore.getChatSearchResults(
                                context.conversationStore.selectedConversation!
                                    .chatTranscription,
                                true, // strict
                            );
                    } else {
                        // Call Transcription, use getSearchResults
                        results = context.transcriptionStore.getSearchResults({
                            blurbsOnly: true,
                            strict: true,
                        });
                    }

                    if (results && results.length > 0) {
                        searchResults.push(
                            ...(results as
                                | ChatSearchResult[]
                                | TranscriptionSearchResult[]),
                        );
                    }
                });
                // Reset search string so it doesn't show as active search in the UI
                context.transcriptionStore.setSearchString("");

                // Sort the results by timestamp/startTime ascending
                // ChatSearchResult has timestamp
                // TranscriptionSearchResult has startTime
                if (context.conversationStore.selectedConversation.isChat) {
                    searchResults.sort((a, b) => {
                        const aResult = a as ChatSearchResult;
                        const bResult = b as ChatSearchResult;
                        return moment(aResult.timestamp).diff(
                            bResult.timestamp,
                        );
                    });
                } else {
                    searchResults.sort((a, b) => {
                        const aResult = a as TranscriptionSearchResult;
                        const bResult = b as TranscriptionSearchResult;
                        return aResult.startTime - bResult.startTime;
                    });
                }
            }

            // remove duplicate search results
            searchResults = searchResults.filter(
                (result, index, self) =>
                    self.findIndex((r) => _.isEqual(r, result)) === index,
            );

            return {
                type: "focusConversationFilters",
                smartBannerMessage,
                searchResults,
                speakerStore: context.conversationStore.speakerStore,
                searchItems,
                transcriptionStore: context.transcriptionStore,
                onClick: context.onClick,
            };
        }
    }

    // Smart Banner Creation Type Not Supported
    return null;
}
